// apps/pwa-pos/src/lib/catalog.ts
// Catálogo offline: snapshot en IndexedDB por (org, sucursal).
//
// Reglas anti-inconsistencia:
// 1. Un solo cliente Supabase (el de `connector`) — un 2º createClient
//    restauraba la sesión async y a veces consultaba RLS como anon → [].
// 2. Nunca sobrescribir un snapshot con items con un refresh vacío “exitoso”
//    (RLS/anon, race de JWT, o red que devuelve data:[] sin error).
// 3. Sin JWT no se toca el cache local.

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@mibodega/db-contract';
import { connector } from './auth';
import { catalogoKey, getDb } from './db';
import type { CatalogoSnapshot } from './queue-types';

export type { CatalogoSnapshot };

export interface CatalogCategory {
  id: string;
  name: string;
  parentId: string | null;
  parentName: string | null;
}

export interface CatalogItem {
  id: string;
  nombre: string;
  codigo_barras?: string | null;
  sku?: string | null;
  imagen_url?: string | null;
  presentacion_id: string;
  presentacion_nombre: string;
  precio: number;
  stock_disponible: number | null;
  /**
   * True when this branch has a row in resumen_stock_sucursal.
   * False when the product was never assigned to the branch.
   * Null when the catalog was loaded without a branch.
   * Missing on snapshots written before this field existed.
   */
  inventario_asignado?: boolean | null;
  /** Missing only in snapshots written before category support. */
  category?: CatalogCategory | null;
}

const TTL_MS = 6 * 60 * 60 * 1000; // 6 horas

/** Test hook — avoids coupling unit tests to the live auth connector. */
let clientOverride: SupabaseClient<Database> | null | undefined;

export function __setCatalogClientForTests(
  client: SupabaseClient<Database> | null | undefined,
): void {
  clientOverride = client;
}

function getClient(): SupabaseClient<Database> | null {
  if (clientOverride !== undefined) return clientOverride;
  return (connector.client as SupabaseClient<Database> | null) ?? null;
}

export async function readCatalog(
  orgId: string | null,
  sucursalId: string | null = null,
): Promise<CatalogoSnapshot | null> {
  try {
    const db = await getDb();
    const snap = await db.get('catalogo', catalogoKey(orgId, sucursalId));
    if (!snap || !Array.isArray(snap.items)) return null;
    return {
      ...snap,
      items: snap.items.map((item) => ({ ...item, category: item.category ?? null })),
    };
  } catch {
    return null;
  }
}

export async function writeCatalog(
  orgId: string | null,
  items: CatalogItem[],
  sucursalId: string | null = null,
): Promise<void> {
  try {
    const [orgKey, sucursalKey] = catalogoKey(orgId, sucursalId);
    const db = await getDb();
    await db.put('catalogo', {
      orgKey,
      sucursalKey,
      items,
      refreshedAt: new Date().toISOString(),
      orgId,
      sucursalId,
    });
  } catch (err) {
    console.warn('[catalog] No se pudo persistir el cache:', err);
  }
}

/**
 * Refresca el catalogo desde Supabase (PostgREST via el cliente autenticado del connector).
 * Si se pasa sucursalId, tambien trae el stock disponible por presentacion
 * desde resumen_stock_sucursal (null = sin dato de stock para esa sucursal).
 * Si falla o el remoto viene vacío con cache local, mantiene el cache anterior.
 */
export type CatalogRefreshResult = {
  items: CatalogItem[];
  source: 'remote' | 'cache';
  reason?: 'no-client' | 'no-session' | 'empty-remote' | 'error';
};

async function ensureAccessToken(client: SupabaseClient<Database>): Promise<string | null> {
  const first = await client.auth.getSession();
  if (first.data.session?.access_token) return first.data.session.access_token;
  const auth = client.auth as {
    refreshSession?: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
  if (typeof auth.refreshSession !== 'function') return null;
  try {
    const refreshed = await auth.refreshSession();
    return refreshed.data.session?.access_token ?? null;
  } catch {
    return null;
  }
}

function isAuthError(err: unknown): boolean {
  if (!err) return false;
  const msg =
    err instanceof Error
      ? err.message
      : typeof err === 'object' && err && 'message' in err
        ? String((err as { message: unknown }).message)
        : String(err);
  return /jwt|unauthorized|401|not authenticated/i.test(msg);
}

export async function refreshCatalog(
  orgId: string | null,
  sucursalId: string | null = null,
  opts: { forceEmpty?: boolean } = {},
): Promise<CatalogItem[]> {
  return (await refreshCatalogDetailed(orgId, sucursalId, opts)).items;
}

/**
 * Igual que refreshCatalog, pero dice si el resultado vino de la red o del
 * cache local. El botón "Refrescar" usa esto para no mentir un éxito.
 */
export async function refreshCatalogDetailed(
  orgId: string | null,
  sucursalId: string | null = null,
  opts: { forceEmpty?: boolean } = {},
): Promise<CatalogRefreshResult> {
  const previous = await readCatalog(orgId, sucursalId);
  const previousItems = previous?.items ?? [];

  const client = getClient();
  if (!client) {
    console.warn('[catalog] cliente Supabase no disponible; usando cache local');
    return { items: previousItems, source: 'cache', reason: 'no-client' };
  }

  try {
    const token = await ensureAccessToken(client);
    if (!token) {
      console.warn('[catalog] sin sesión JWT; no se sobrescribe cache local');
      return { items: previousItems, source: 'cache', reason: 'no-session' };
    }

    const fetchRows = () => {
      const productosQ = client
        .from('productos_master')
        .select(
          'id, nombre, codigo_barras, sku, imagen_url, activo, requiere_lote, es_fraccionable, categoria_id',
        )
        .eq('activo', true);
      const presentacionesQ = client
        .from('presentaciones_master')
        .select('id, producto_id, nombre, codigo, precio_venta_sugerido, activo, es_unidad_base')
        .eq('activo', true);
      const categoriasQ = client
        .from('categorias_master')
        .select('id, nombre, categoria_padre_id, activo')
        .eq('activo', true);
      return Promise.all([
        (orgId ? productosQ.eq('organization_id', orgId) : productosQ).limit(2000),
        (orgId ? presentacionesQ.eq('organization_id', orgId) : presentacionesQ).limit(5000),
        (orgId ? categoriasQ.eq('organization_id', orgId) : categoriasQ).limit(1000),
      ]);
    };

    let [
      { data: productos, error: e1 },
      { data: presentaciones, error: e2 },
      { data: categorias, error: eCategories },
    ] = await fetchRows();

    if (isAuthError(e1) || isAuthError(e2) || isAuthError(eCategories)) {
      await ensureAccessToken(client);
      const retried = await fetchRows();
      productos = retried[0].data;
      e1 = retried[0].error;
      presentaciones = retried[1].data;
      e2 = retried[1].error;
      categorias = retried[2].data;
      eCategories = retried[2].error;
    }

    if (e1) throw e1;
    if (e2) throw e2;
    if (eCategories) throw eCategories;

    const categoryRows = categorias ?? [];
    const categoryNameById = new Map(
      categoryRows.map((category) => [category.id, category.nombre]),
    );
    const categoryById = new Map<string, CatalogCategory>(
      categoryRows.map((category) => [
        category.id,
        {
          id: category.id,
          name: category.nombre,
          parentId: category.categoria_padre_id,
          parentName: category.categoria_padre_id
            ? (categoryNameById.get(category.categoria_padre_id) ?? null)
            : null,
        },
      ]),
    );

    const stockByPresentacion = new Map<string, number>();
    if (sucursalId) {
      const stockBase = client
        .from('resumen_stock_sucursal')
        .select('presentacion_id, cantidad_base')
        .eq('sucursal_id', sucursalId);
      const { data: stockRows, error: e3 } = await (
        orgId ? stockBase.eq('organization_id', orgId) : stockBase
      ).limit(10000);
      if (e3) throw e3;
      for (const row of stockRows ?? []) {
        stockByPresentacion.set(row.presentacion_id as string, Number(row.cantidad_base ?? 0));
      }
    }

    const items: CatalogItem[] = (presentaciones ?? [])
      .map((pr): CatalogItem | null => {
        const prod = (productos ?? []).find((p) => p.id === pr.producto_id);
        if (!prod) return null;
        return {
          id: prod.id,
          nombre: prod.nombre,
          codigo_barras: prod.codigo_barras ?? null,
          sku: prod.sku ?? null,
          imagen_url: prod.imagen_url ?? null,
          presentacion_id: pr.id,
          presentacion_nombre: pr.nombre,
          precio: Number(pr.precio_venta_sugerido ?? 0),
          stock_disponible: sucursalId ? (stockByPresentacion.get(pr.id) ?? 0) : null,
          inventario_asignado: sucursalId ? stockByPresentacion.has(pr.id) : null,
          category: prod.categoria_id ? (categoryById.get(prod.categoria_id) ?? null) : null,
        };
      })
      .filter((x): x is CatalogItem => x !== null);

    // Protege el POS: un [] “exitoso” (anon/RLS/race) no debe borrar el catálogo
    // que el cajero ya tenía en IndexedDB.
    if (items.length === 0 && previousItems.length > 0 && !opts.forceEmpty) {
      console.warn(
        `[catalog] remoto vacío con ${previousItems.length} items locales — se conserva cache`,
      );
      return { items: previousItems, source: 'cache', reason: 'empty-remote' };
    }

    await writeCatalog(orgId, items, sucursalId);
    return { items, source: 'remote' };
  } catch (err) {
    console.warn('[catalog] refresh fallo, usando cache:', err);
    return { items: previousItems, source: 'cache', reason: 'error' };
  }
}

/**
 * Lectura principal desde la UI: intenta traer fresco y caer al cache.
 */
export async function loadCatalog(
  orgId: string | null,
  sucursalId: string | null = null,
): Promise<CatalogItem[]> {
  return refreshCatalog(orgId, sucursalId);
}

/** Metadatos utiles para mostrar en UI. */
export async function catalogStatus(
  orgId: string | null,
  sucursalId: string | null = null,
): Promise<{
  count: number;
  refreshedAt: string | null;
  isStale: boolean;
}> {
  const cached = await readCatalog(orgId, sucursalId);
  if (!cached) return { count: 0, refreshedAt: null, isStale: true };
  const age = Date.now() - new Date(cached.refreshedAt).getTime();
  return {
    count: cached.items.length,
    refreshedAt: cached.refreshedAt,
    isStale: age > TTL_MS,
  };
}
